// mcn-frontend/src/components/InvestmentOpportunities.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { API_ENDPOINTS } from '../config';
import { TrendingUp, Users, DollarSign, Calendar, Target, CheckCircle, AlertCircle, Clock } from 'lucide-react';

const InvestmentOpportunities = () => {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(API_ENDPOINTS.investments.opportunities);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Ensure we have an array
      const projectsArray = Array.isArray(data) 
        ? data 
        : (data.opportunities || data.projects || []);
      
      setProjects(projectsArray);
      console.log('Fetched projects:', projectsArray.length);
    } catch (err) {
      console.error('Error fetching investment opportunities:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter projects based on active tab
  const getFilteredProjects = () => {
    let filtered = [...projects];

    // Filter by tab
    if (activeTab === 'funding') {
      filtered = filtered.filter(p => 
        p.status === 'FUNDING' || 
        p.status === 'ACTIVE' || 
        p.status === 'FUNDING_COMPLETE'
      );
    } else if (activeTab === 'revenue') {
      filtered = filtered.filter(p => p.status === 'GENERATING_REVENUE');
    } else if (activeTab === 'network') {
      filtered = filtered.filter(p => 
        p.status === 'GENERATING_REVENUE' && p.targetChannelId !== null
      );
    } else if (activeTab === 'external') {
      filtered = filtered.filter(p => 
        p.status === 'GENERATING_REVENUE' && p.targetChannelId === null
      );
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(search) ||
        p.description?.toLowerCase().includes(search) ||
        p.category?.toLowerCase().includes(search)
      );
    }

    // Sort projects
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case 'funding':
        filtered.sort((a, b) => (b.fundingGoal - b.fundingRaised) - (a.fundingGoal - a.fundingRaised));
        break;
      case 'roi':
        filtered.sort((a, b) => (b.expectedReturn || 0) - (a.expectedReturn || 0));
        break;
      default:
        break;
    }

    return filtered;
  };

  const filteredProjects = getFilteredProjects();

  // Statistics
  const stats = {
    total: projects.length,
    funding: projects.filter(p => p.status === 'FUNDING' || p.status === 'ACTIVE').length,
    revenue: projects.filter(p => p.status === 'GENERATING_REVENUE').length,
    network: projects.filter(p => p.status === 'GENERATING_REVENUE' && p.targetChannelId !== null).length,
    external: projects.filter(p => p.status === 'GENERATING_REVENUE' && p.targetChannelId === null).length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading investment opportunities...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md">
          <div className="flex items-center text-red-600 mb-4">
            <AlertCircle className="mr-2" />
            <h2 className="text-xl font-bold">Error Loading Projects</h2>
          </div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchProjects}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Investment Opportunities</h1>
          <p className="text-xl text-blue-100">
            Invest in content creation and share in revenue from successful YouTube projects
          </p>
          
          {!currentUser && (
            <div className="mt-6 bg-blue-700 bg-opacity-50 p-4 rounded-lg">
              <p className="text-sm">
                👋 <strong>New here?</strong> Browse all opportunities below. 
                <button
                  onClick={() => navigate('/login')}
                  className="underline ml-2 hover:text-white"
                >
                  Login to invest
                </button>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="max-w-7xl mx-auto px-4 -mt-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <StatCard
            icon={<Target />}
            label="Total Projects"
            value={stats.total}
            color="bg-blue-600"
          />
          <StatCard
            icon={<DollarSign />}
            label="Funding Projects"
            value={stats.funding}
            color="bg-green-600"
          />
          <StatCard
            icon={<TrendingUp />}
            label="Revenue Projects"
            value={stats.revenue}
            color="bg-purple-600"
          />
          <StatCard
            icon={<CheckCircle />}
            label="Network Content"
            value={stats.network}
            color="bg-indigo-600"
          />
          <StatCard
            icon={<Users />}
            label="External Content"
            value={stats.external}
            color="bg-pink-600"
          />
        </div>
      </div>

      {/* Filters and Tabs */}
      <div className="max-w-7xl mx-auto px-4 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2 mb-6">
            <TabButton
              active={activeTab === 'all'}
              onClick={() => setActiveTab('all')}
              label="All Projects"
              count={stats.total}
            />
            <TabButton
              active={activeTab === 'funding'}
              onClick={() => setActiveTab('funding')}
              label="Funding"
              count={stats.funding}
            />
            <TabButton
              active={activeTab === 'revenue'}
              onClick={() => setActiveTab('revenue')}
              label="Revenue Generating"
              count={stats.revenue}
            />
            <TabButton
              active={activeTab === 'network'}
              onClick={() => setActiveTab('network')}
              label="Network Content"
              count={stats.network}
            />
            <TabButton
              active={activeTab === 'external'}
              onClick={() => setActiveTab('external')}
              label="External Content"
              count={stats.external}
            />
          </div>

          {/* Search and Sort */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="funding">Most Funding Needed</option>
              <option value="roi">Highest ROI</option>
            </select>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        {filteredProjects.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No projects found</h3>
            <p className="text-gray-600">
              Try adjusting your filters or search terms
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map(project => (
              <ProjectCard 
                key={project.id} 
                project={project} 
                currentUser={currentUser}
                navigate={navigate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Statistics Card Component
const StatCard = ({ icon, label, value, color }) => (
  <div className="bg-white rounded-lg shadow-md p-6">
    <div className={`${color} w-12 h-12 rounded-lg flex items-center justify-center text-white mb-3`}>
      {icon}
    </div>
    <div className="text-2xl font-bold text-gray-900">{value}</div>
    <div className="text-sm text-gray-600">{label}</div>
  </div>
);

// Tab Button Component
const TabButton = ({ active, onClick, label, count }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
      active
        ? 'bg-blue-600 text-white'
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }`}
  >
    {label} <span className="ml-1 text-sm">({count})</span>
  </button>
);

// Project Card Component
const ProjectCard = ({ project, currentUser, navigate }) => {
  const isRevenueGenerating = project.status === 'GENERATING_REVENUE';
  const isNetwork = project.targetChannelId !== null;
  const isExternal = !isNetwork && isRevenueGenerating;
  
  const handleInvestClick = () => {
    if (!currentUser) {
      // Not logged in - redirect to login with return path
      navigate('/login', { 
        state: { from: `/project/${project.id}` } 
      });
    } else {
      // Logged in - go directly to project details
      navigate(`/project/${project.id}`);
    }
  };

  // Calculate funding progress
  const fundingProgress = project.fundingGoal > 0 
    ? (project.fundingRaised / project.fundingGoal) * 100 
    : 0;

  // Get status badge
  const getStatusBadge = () => {
    if (isRevenueGenerating) {
      if (isNetwork) {
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
            <TrendingUp className="w-3 h-3 mr-1" />
            Network Content
          </span>
        );
      } else if (isExternal) {
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            <DollarSign className="w-3 h-3 mr-1" />
            External Revenue
          </span>
        );
      }
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <Target className="w-3 h-3 mr-1" />
          Funding
        </span>
      );
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow">
      {/* Project Image */}
      <div className="h-48 bg-gradient-to-r from-blue-400 to-purple-500 relative">
        {project.thumbnailUrl ? (
          <img
            src={project.thumbnailUrl}
            alt={project.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white text-6xl font-bold">
            {project.title.charAt(0)}
          </div>
        )}
        <div className="absolute top-4 right-4">
          {getStatusBadge()}
        </div>
      </div>

      {/* Project Content */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
        
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {project.description || 'No description available'}
        </p>

        {/* Category */}
        {project.category && (
          <div className="flex items-center text-sm text-gray-500 mb-4">
            <span className="bg-gray-100 px-2 py-1 rounded">
              {project.category}
            </span>
          </div>
        )}

        {/* Funding Information for Non-Revenue Projects */}
        {!isRevenueGenerating && (
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Funding Progress</span>
              <span className="font-semibold text-gray-900">
                ${project.fundingRaised?.toLocaleString() || 0} / ${project.fundingGoal?.toLocaleString() || 0}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${Math.min(fundingProgress, 100)}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{fundingProgress.toFixed(0)}% funded</span>
              <span>{project.investorCount || 0} investors</span>
            </div>
          </div>
        )}

        {/* Revenue Information for Revenue-Generating Projects */}
        {isRevenueGenerating && (
          <div className="mb-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Expected ROI</span>
              <span className="font-semibold text-green-600">
                {project.expectedReturn || 15}%
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Min. Investment</span>
              <span className="font-semibold text-gray-900">
                ${project.minInvestment?.toLocaleString() || 100}
              </span>
            </div>
            {project.monthlyRevenue && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Monthly Revenue</span>
                <span className="font-semibold text-gray-900">
                  ${project.monthlyRevenue.toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}

       {/* Timeline Information */}
        {!isRevenueGenerating && project.fundingDeadline && (
          <div className="flex items-center text-sm text-gray-500 mb-4">
            <Clock className="w-4 h-4 mr-1" />
            <span>
              Ends {new Date(project.fundingDeadline).toLocaleDateString()}
            </span>
          </div>
        )}
        
        {/* Project Created Date */}
        {project.createdAt && (
          <div className="flex items-center text-sm text-gray-500 mb-4">
            <Calendar className="w-4 h-4 mr-1" />
            <span>
              Created {new Date(project.createdAt).toLocaleDateString()}
            </span>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={handleInvestClick}
          className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          {!currentUser ? (
            'Login to Invest'
          ) : isRevenueGenerating ? (
            'View Details'
          ) : (
            'Invest Now'
          )}
        </button>
      </div>
    </div>
  );
};

export default InvestmentOpportunities;